import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Award,
  Briefcase,
  Code,
  ChevronDown,
  Calendar,
  Target,
  CheckCircle2,
  Building2,
  GraduationCap,
  Timer,
  LineChart
} from 'lucide-react';
import globoLogo from '../../assets/Logo_globo_principal-laranja.png'
import sysLogo from '../../assets/LOGO-Sys-Manager-horizontal-COLOR.png'
import somosLogo from '../../assets/Somos_Educação_logo.png'
import logo2v from '../../assets/logo-light.svg'

interface CareerStep {
  company: string;
  role: string;
  period: string;
  description: string;
  technologies: string[];
  achievements: string[];
  logos: {
    src: string;
    isPng?: boolean;
  }[];
  type: 'education' | 'business' | 'startup';
}

const careerSteps: CareerStep[] = [
  {
    company: "Enterprise Z",
    role: "CEO & Founder",
    period: "2022 - Presente",
    description: "Startup de educação focada em tecnologia e programação e na inserção de cada vez mais pessoas capacitadas nas áreas de tecnologia. Liderei o desenvolvimento e implementação da plataforma online para ensino de habilidades técnicas.",
    technologies: ["React", "Scrum", "Firebase", "FireStore"],
    achievements: [
      "Criação da nossa Plataforma de ensino Próprio - Next Devs",
      "Desenvolvimento do Projeto de ensino com aulas ao vivo - inicianTI",
      "Prestação de Serviço de criação de Sites para empreendedores - SyTI"
    ],
    logos: [{ src: logo2v }],
    type: 'startup'
  },
  {
    company: "Tech Company X",
    role: "Desenvolvedor Java",
    period: "2023 - Presente",
    description: "Desenvolvedor e um dos responsáveis pela modernização do sistema de faturamento da Globo.",
    technologies: ["Java", "PL/SQL", "Angular", "TypeScript"],
    achievements: [
      "Participação da modernização da plataforma WR",
      "Workshop em Desenvolvimento OutSystems"
    ],
    logos: [
      { src: sysLogo, isPng: true },
      { src: globoLogo, isPng: true }
    ],
    type: 'business'
  },
  {
    company: "Startup Y",
    role: "Analista de Sistemas I",
    period: "2022 - 2022",
    description: "Responsável por desenvolver automações para o setor Logístico da maior empresa de educação básico do Brasil.",
    technologies: ["Python", "PL/SQL", "JavaScript"],
    achievements: [
      "Migração dos fluxos para nova arquitetura baseada em Microfrontends e Microserviços",
      "Desenvolvimento de automações em Python"
    ],
    logos: [{ src: somosLogo, isPng: true }],
    type: 'education'
  }
];

const typeConfig = {
  education: {
    icon: GraduationCap,
    gradient: 'from-blue-400 to-blue-600'
  },
  business: {
    icon: Building2,
    gradient: 'from-blue-500 to-blue-700'
  },
  startup: {
    icon: LineChart,
    gradient: 'from-blue-300 to-blue-500'
  }
};

interface CareerCardProps {
  step: CareerStep;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}

const CareerCard: React.FC<CareerCardProps> = ({ step, index, isExpanded, onToggle }) => {
  const TypeIcon = typeConfig[step.type].icon;
  const gradient = typeConfig[step.type].gradient;

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.2 }}
      className="relative mb-12 last:mb-0"
    >
      {index !== careerSteps.length - 1 && (
        <motion.div 
          className={`absolute left-8 top-16 bottom-0 w-0.5 bg-gradient-to-b ${gradient}`}
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ duration: 1, delay: index * 0.2 }}
        />
      )}
      
      <div className="relative flex gap-6">
        <motion.div className="relative" whileHover={{ scale: 1.1 }}>
          <div className={`w-16 h-16 rounded-full bg-black/80 border-2 bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg backdrop-blur-sm`}>
            <TypeIcon className="w-6 h-6 text-white" />
          </div>
        </motion.div>
        
        <div className="flex-1">
          <motion.div 
            className={`bg-black/50 backdrop-blur-sm rounded-xl p-6 border border-blue-900/30 transition-all duration-300 ${
              isExpanded ? 'shadow-lg shadow-blue-500/10' : ''
            }`}
            whileHover={{ y: -2 }}
          >
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
              <div className="space-y-3">
                <h3 className={`text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r ${gradient}`}>
                  {step.role}
                </h3>
                <div className="flex flex-wrap items-center gap-4">
                  {step.logos.map((logo, logoIndex) => (
                    <motion.img 
                      key={logoIndex} 
                      src={logo.src} 
                      alt={`Logo ${logoIndex + 1}`} 
                      className={`object-contain ${logo.isPng ? 'h-10' : 'h-6'}`}
                      whileHover={{ scale: 1.05 }}
                    />
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 text-blue-300 bg-blue-900/20 px-3 py-1.5 rounded-full text-xs backdrop-blur-sm border border-blue-800/30">
                <Calendar className="w-3.5 h-3.5" />
                {step.period}
              </div>
            </div>
            
            <AnimatePresence>
              {isExpanded && (
                <motion.div 
                  className="space-y-4"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <p className="text-blue-200 text-sm leading-relaxed">
                    {step.description}
                  </p>
                  
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Code className="w-4 h-4 text-blue-400" />
                      <h4 className="text-xs font-medium text-blue-300">
                        Tecnologias e Ferramentas
                      </h4>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {step.technologies.map((tech, techIndex) => (
                        <motion.span 
                          key={techIndex}
                          whileHover={{ scale: 1.05 }}
                          className={`px-2.5 py-1 text-xs rounded-md bg-blue-900/20 border border-blue-800/30 text-blue-300 hover:bg-blue-800/30 transition-colors duration-300`}
                        >
                          {tech}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-4 h-4 text-blue-400" />
                      <h4 className="text-xs font-medium text-blue-300">
                        Principais Realizações
                      </h4>
                    </div>
                    <ul className="space-y-2">
                      {step.achievements.map((achievement, achievementIndex) => (
                        <motion.li 
                          key={achievementIndex}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: achievementIndex * 0.1 }}
                          className="flex items-start gap-2 group"
                        >
                          <CheckCircle2 className="w-4 h-4 mt-0.5 text-blue-500 flex-shrink-0" />
                          <span className="text-blue-200 text-sm">
                            {achievement}
                          </span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <motion.button
              onClick={onToggle}
              className="mt-4 w-full flex items-center justify-center text-blue-400 hover:text-blue-300 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <ChevronDown className={`w-5 h-5 transform transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
            </motion.button>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

const Career: React.FC = () => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);

  return (
    <section id='career' className="relative py-20">
      <div className="absolute inset-0 bg-gradient-to-b from-black via-blue-900/20 to-black" />
      
      <div className="container mx-auto px-4 relative">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto text-center mb-16"
        >
          <motion.span 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/20 text-blue-400 text-sm mb-4"
            whileHover={{ scale: 1.05 }}
          >
            <Briefcase className="w-5 h-5" />
            <span>Experiência Profissional</span>
          </motion.span>
          
          <h2 className="text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-blue-600 mb-6">
            Minha Jornada Profissional
          </h2>
          <p className="text-blue-200 text-lg max-w-2xl mx-auto">
            Explore minha trajetória e principais conquistas ao longo dos anos, 
            demonstrando meu crescimento e impacto em diferentes áreas.
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          {careerSteps.map((step, index) => (
            <CareerCard
              key={index}
              step={step}
              index={index}
              isExpanded={expandedIndex === index}
              onToggle={() => setExpandedIndex(expandedIndex === index ? null : index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Career;